/* =============================================================
   Analytics.jsx — Exchange analytics dashboard (admin)
   -------------------------------------------------------------
   Visualises historical exchange data with:
   - Grouped bar chart: outgoing vs incoming per year/semester
   - Partner exchange balance table with search, sort, pagination
   - Top sending partners + partnerships to rebalance panels

   ✓ DONE (backend integration):
   - Replaced all hardcoded semesterRecords / partners arrays with
     useAnalytics() hook that queries Supabase in real time
   - Semester derived from students.created_at (Jan–Jun = Spring,
     Jul–Dec = Fall) — no schema change required
   - Partner stats aggregated from students joined to partner_universities
   - Active Partners count comes from partner_universities table
   - Year filter options derived dynamically from loaded data
   - CSV export uses live data
   ============================================================= */

import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAnalytics } from '../hooks/useAnalytics';
import { useArchive }   from '../hooks/useArchive';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const balanceFilterFn = {
  Surplus:  (p) => p.balance > 0,
  Deficit:  (p) => p.balance < 0,
  Balanced: (p) => p.balance === 0,
  All:      () => true,
};

/* ─── Helpers ────────────────────────────────────────── */
function trendLabel(balance) {
  if (balance > 0) return { icon: '↑', label: 'Surplus', cls: 'text-emerald-600' };
  if (balance < 0) return { icon: '↓', label: 'Deficit', cls: 'text-red-500' };
  return { icon: '→', label: 'Balanced', cls: 'text-slate-400' };
}
function balanceColor(b) {
  if (b > 0) return 'text-emerald-600 font-semibold';
  if (b < 0) return 'text-red-500 font-semibold';
  return 'text-slate-400 font-semibold';
}

/* ─── Chart data builder ─────────────────────────────── */
// termId = '' means all terms; otherwise filter to that one term.
function buildChartData(records, termId) {
  const recs = termId ? records.filter(r => r.termId === termId) : records;
  return recs.map(r => ({ label: r.termName, Sent: r.sent, Received: r.received }));
}

/* ─── Stat totals from filtered records ─────────────── */
function calcTotals(records, termId) {
  const recs    = termId ? records.filter(r => r.termId === termId) : records;
  const sent     = recs.reduce((s, r) => s + r.sent,     0);
  const received = recs.reduce((s, r) => s + r.received, 0);
  return { sent, received, balance: sent - received };
}

/* ─── CSV export ─────────────────────────────────────── */
function exportCSV(partnerStats, termName) {
  const headers = ['University', 'Country', 'Type', 'Total Sent', 'Total Received', 'Balance'];
  const rows = partnerStats.map(p => [
    p.name,
    p.country,
    p.type,
    p.sent,
    p.received,
    p.balance > 0 ? `+${p.balance}` : p.balance,
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `exchange_analytics_${(termName || 'all_terms').replace(/\s+/g, '_')}_unixplore.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ─── Custom Tooltip ─────────────────────────────────── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0b1c30] text-white text-xs rounded-xl shadow-xl px-4 py-3 space-y-1">
      <p className="font-bold text-sm mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: entry.fill }}></span>
          <span className="text-slate-300">{entry.name}:</span>
          <span className="font-semibold">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Highlight helper ───────────────────────────────── */
function highlight(text, query) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-indigo-100 text-indigo-700 rounded px-0.5 not-italic font-semibold">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

/* ─── Loading skeleton ───────────────────────────────── */
function Skeleton({ className }) {
  return <div className={`animate-pulse bg-slate-100 rounded-xl ${className}`} />;
}

/* ─── Archive section ────────────────────────────────── */
function ArchiveSection() {
  const {
    terms, students, loading, error,
    mutating, mutError, restoreTerm, deleteTerm,
  } = useArchive();

  const [activeTerm,     setActiveTerm]     = useState(null);
  const [typeFilter,     setTypeFilter]     = useState('all');
  const [search,         setSearch]         = useState('');
  const [confirmRestore, setConfirmRestore] = useState(null); // term key
  const [confirmDelete,  setConfirmDelete]  = useState(null); // term key
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Auto-select first (newest) term on load
  const selectedTerm = activeTerm ?? terms[0] ?? null;

  const filtered = useMemo(() => {
    if (!selectedTerm) return [];
    return students.filter(s => {
      const match = selectedTerm.semesterId === null
        ? s.semester_id === null
        : s.semester_id === selectedTerm.semesterId;
      if (!match) return false;
      if (typeFilter !== 'all' && s.type !== typeFilter) return false;
      if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [students, selectedTerm, typeFilter, search]);

  if (error) return (
    <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
      <span className="material-symbols-outlined text-[16px]">error</span>{error}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Term selector */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-indigo-600">archive</span>
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">Archived Semesters</h2>
        </div>
        <div className="p-4">
          {/* Mutation error banner */}
          {mutError && (
            <div className="flex items-center gap-2 px-4 py-2.5 mb-2 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              <span className="material-symbols-outlined text-[16px]">error</span>{mutError}
            </div>
          )}

          {loading ? (
            <div className="space-y-2 animate-pulse">
              {[1,2].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl" />)}
            </div>
          ) : terms.length === 0 ? (
            <div className="text-center py-8">
              <span className="material-symbols-outlined text-slate-300 text-[36px]">archive</span>
              <p className="text-sm text-slate-400 mt-2">No archived semesters yet.</p>
              <p className="text-xs text-slate-400 mt-1">Close a term in Settings → Exchange Terms to start archiving.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {terms.map(t => {
                const key      = t.semesterId ?? '__unassigned__';
                const selKey   = selectedTerm ? (selectedTerm.semesterId ?? '__unassigned__') : null;
                const isActive = key === selKey;
                const total    = t.incoming + t.outgoing;
                const isBusy   = mutating === key;
                const isConfirmingRestore = confirmRestore === key;
                const isConfirmingDelete  = confirmDelete  === key;

                return (
                  <div
                    key={key}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                      isActive ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'
                    }`}
                    onClick={() => { setActiveTerm(t); setSearch(''); setTypeFilter('all'); setConfirmRestore(null); setConfirmDelete(null); }}
                  >
                    {/* Left: term info */}
                    <div>
                      <p className={`text-sm font-semibold ${isActive ? 'text-indigo-700' : 'text-[#0b1c30]'}`}>
                        {t.name}
                      </p>
                      <p className={`text-xs mt-0.5 ${isActive ? 'text-indigo-500' : 'text-slate-400'}`}>
                        {total} student{total !== 1 ? 's' : ''}
                        <span className="mx-1.5 opacity-50">·</span>
                        {t.incoming} incoming · {t.outgoing} outgoing
                      </p>
                    </div>

                    {/* Right: actions */}
                    <div
                      className="flex items-center gap-2 flex-shrink-0"
                      onClick={e => e.stopPropagation()} // don't trigger card selection
                    >
                      {isBusy ? (
                        <span className="material-symbols-outlined text-[18px] text-slate-400 animate-spin">progress_activity</span>
                      ) : isConfirmingRestore ? (
                        <>
                          <span className="text-xs text-slate-500">Restore all {total} students?</span>
                          <button
                            onClick={() => setConfirmRestore(null)}
                            className="px-2.5 py-1.5 text-xs font-medium border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-100 transition-colors"
                          >Cancel</button>
                          <button
                            onClick={() => { setConfirmRestore(null); restoreTerm(t.semesterId); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[13px]">restore</span>
                            Confirm
                          </button>
                        </>
                      ) : isConfirmingDelete ? (
                        <>
                          <span className="text-xs text-red-500 font-medium">Delete {total} students permanently?</span>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="px-2.5 py-1.5 text-xs font-medium border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-100 transition-colors"
                          >Cancel</button>
                          <button
                            onClick={() => { setConfirmDelete(null); deleteTerm(t.semesterId); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[13px]">delete_forever</span>
                            Delete forever
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Restore */}
                          <button
                            onClick={() => { setConfirmDelete(null); setConfirmRestore(key); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                            title="Restore all students in this term to active lists"
                          >
                            <span className="material-symbols-outlined text-[14px]">restore</span>
                            Restore
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => { setConfirmRestore(null); setConfirmDelete(key); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                            title="Permanently delete all students in this term"
                          >
                            <span className="material-symbols-outlined text-[14px]">delete_forever</span>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Student list for selected term */}
      {selectedTerm && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">
                {selectedTerm.name} — Students
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {selectedTerm.incoming} incoming · {selectedTerm.outgoing} outgoing
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Type filter */}
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                {[
                  { value: 'all',      label: 'All'      },
                  { value: 'incoming', label: '← Incoming' },
                  { value: 'outgoing', label: 'Outgoing →' },
                ].map(f => (
                  <button
                    key={f.value}
                    onClick={() => setTypeFilter(f.value)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      typeFilter === f.value
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {/* Search */}
              <div className="relative">
                <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[15px]">search</span>
                <input
                  type="text"
                  placeholder="Search name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 w-44"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                {['Name', 'Type', 'University', 'Nationality', 'Exchange Type', 'EU Status'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 first:pl-6">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3.5 first:pl-6">
                        <div className="h-4 bg-slate-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-400">
                    {search ? 'No students match your search.' : 'No students in this term.'}
                  </td>
                </tr>
              ) : (
                filtered.map(s => (
                  <tr key={s.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedStudent(s)}>
                    <td className="pl-6 pr-4 py-3.5 text-sm font-medium text-[#0b1c30]">{s.name}</td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        s.type === 'incoming'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {s.type === 'incoming' ? '← Incoming' : 'Outgoing →'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[#464555]">
                      {s.type === 'incoming'
                        ? (s.home_university        ?? s.partner_universities?.name ?? '—')
                        : (s.destination_university ?? s.partner_universities?.name ?? '—')
                      }
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[#464555]">{s.nationality ?? s.country ?? '—'}</td>
                    <td className="px-4 py-3.5 text-sm text-[#464555] capitalize">
                      {s.exchange_type?.replace('_', ' ') ?? '—'}
                    </td>
                    <td className="px-4 py-3.5">
                      {s.eu_status ? (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          s.eu_status === 'eu'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}>
                          {s.eu_status === 'eu' ? 'EU' : 'Extra-EU'}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!loading && filtered.length > 0 && (
            <div className="px-6 py-3 border-t border-slate-100">
              <p className="text-xs text-slate-400">{filtered.length} student{filtered.length !== 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Archived student detail drawer ─────────────────────── */}
      {selectedStudent && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setSelectedStudent(null)}
          />
          {/* Drawer */}
          <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                  Archived Student
                </p>
                <h3 className="text-base font-semibold text-[#0b1c30] truncate">{selectedStudent.name}</h3>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                    selectedStudent.type === 'incoming'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    {selectedStudent.type === 'incoming' ? '← Incoming' : 'Outgoing →'}
                  </span>
                  {selectedStudent.eu_status && (
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                      selectedStudent.eu_status === 'eu'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-amber-50 text-amber-600'
                    }`}>
                      {selectedStudent.eu_status === 'eu' ? 'EU' : 'Extra-EU'}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedStudent(null)}
                className="ml-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors flex-shrink-0"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {/* Term */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Exchange Term</p>
                <p className="text-sm text-[#0b1c30]">
                  {selectedStudent.semesters?.name ?? 'Unassigned'}
                </p>
              </div>

              {/* University */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                  {selectedStudent.type === 'incoming' ? 'Home University' : 'Destination University'}
                </p>
                <p className="text-sm text-[#0b1c30]">
                  {selectedStudent.type === 'incoming'
                    ? (selectedStudent.home_university ?? '—')
                    : (selectedStudent.destination_university ?? '—')}
                </p>
              </div>

              {/* Partner university — only show if different from the university already displayed */}
              {(() => {
                const partnerName = selectedStudent.partner_universities?.name;
                const shownUni = selectedStudent.type === 'incoming'
                  ? selectedStudent.home_university
                  : selectedStudent.destination_university;
                if (!partnerName || partnerName === shownUni) return null;
                return (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Partner University</p>
                    <p className="text-sm text-[#0b1c30]">{partnerName}</p>
                  </div>
                );
              })()}

              {/* Email */}
              {selectedStudent.email && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Email</p>
                  <a
                    href={`mailto:${selectedStudent.email}`}
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    {selectedStudent.email}
                  </a>
                </div>
              )}

              {/* Nationality */}
              {(selectedStudent.nationality || selectedStudent.country) && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Nationality</p>
                  <p className="text-sm text-[#0b1c30]">{selectedStudent.nationality ?? selectedStudent.country}</p>
                </div>
              )}

              {/* Exchange type */}
              {selectedStudent.exchange_type && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Exchange Type</p>
                  <p className="text-sm text-[#0b1c30] capitalize">{selectedStudent.exchange_type.replace('_', ' ')}</p>
                </div>
              )}

            </div>
          </div>
        </>
      )}
    </div>
  );
}


/* ─── Component ─────────────────────────────────────── */
export default function Analytics() {
  const navigate  = useNavigate();
  const searchRef = useRef(null);

  const { semesterRecords, semesters, partnerStats, partnerCount, loading, error } = useAnalytics();

  const [activeTab,   setActiveTab]   = useState('analytics'); // 'analytics' | 'archive'
  const [search,      setSearch]      = useState('');
  const [filter,      setFilter]      = useState('All');
  const [balanceSort, setBalanceSort] = useState('');
  const [termId,      setTermId]      = useState(''); // '' = All Terms
  const [page,        setPage]        = useState(1);
  const PER_PAGE = 5;

  const chartData = useMemo(() => buildChartData(semesterRecords, termId), [semesterRecords, termId]);
  const totals    = useMemo(() => calcTotals(semesterRecords, termId),     [semesterRecords, termId]);

  // Top 5 sending partners (by received count — partners that send most students to us)
  const topSenders = useMemo(() =>
    [...partnerStats]
      .sort((a, b) => b.received - a.received)
      .slice(0, 5)
      .map(p => ({ name: p.name, sent: p.received })),
    [partnerStats]
  );

  // Partners with negative balance (we owe them students)
  const toRebalance = useMemo(() =>
    partnerStats
      .filter(p => p.balance < 0)
      .sort((a, b) => a.balance - b.balance)
      .slice(0, 5)
      .map(p => ({ name: p.name, deficit: p.balance })),
    [partnerStats]
  );

  const filteredPartners = partnerStats
    .filter(p => p.name.toLowerCase().includes(search.toLowerCase()) && balanceFilterFn[filter](p))
    .sort((a, b) => {
      if (balanceSort === 'lowest')  return a.balance - b.balance;
      if (balanceSort === 'highest') return b.balance - a.balance;
      return 0;
    });
  const totalPages        = Math.ceil(filteredPartners.length / PER_PAGE);
  const paginatedPartners = filteredPartners.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Subtitle for chart — shows active filter context
  const activeTermName = termId ? (semesters.find(s => s.id === termId)?.name ?? 'Selected term') : null;
  const periodLabel    = activeTermName ?? 'All time';

  const balanceNote =
    totals.balance < 0 ? 'More received than sent' :
    totals.balance > 0 ? 'More sent than received' :
    'Perfectly balanced';

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-red-500 text-sm">Failed to load analytics: {error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* ── Page Header ───────────────────────────── */}
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">Exchange Analytics</h1>
          <p className="text-[#464555] mt-1">Historical exchange data across all partner universities</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Analytics / Archive tab switcher */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {[
              { value: 'analytics', label: 'Analytics', icon: 'show_chart' },
              { value: 'archive',   label: 'Archive',   icon: 'archive'    },
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.value
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <span className="material-symbols-outlined text-[16px]">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Analytics-only controls */}
          {activeTab === 'analytics' && <>
            <select
              value={termId}
              onChange={e => setTermId(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
            >
              <option value="">All Terms</option>
              {semesters.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button
              onClick={() => exportCSV(partnerStats, activeTermName)}
              disabled={loading || partnerStats.length === 0}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[16px]">download</span>
              Export CSV
            </button>
          </>}
        </div>
      </div>

      {/* ── Archive tab ──────────────────────────── */}
      {activeTab === 'archive' && <ArchiveSection />}

      {/* ── Analytics tab ────────────────────────── */}
      {activeTab === 'analytics' && <>

      {/* Active filter pill */}
      {termId && (
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-xs text-slate-500 font-medium">Showing:</span>
          <span className="flex items-center gap-1.5 text-xs font-semibold bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full">
            {activeTermName}
            <button onClick={() => setTermId('')} className="hover:text-indigo-900 ml-0.5">×</button>
          </span>
          <button
            onClick={() => setTermId('')}
            className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2"
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Section 1: Stat cards ─────────────────── */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {/* Total Sent */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-indigo-600 text-[20px]">flight_takeoff</span>
          </div>
          {loading
            ? <Skeleton className="h-9 w-16 mb-1" />
            : <p className="font-display text-3xl font-bold text-[#0b1c30]">{totals.sent}</p>
          }
          <p className="text-[#464555] text-sm mt-1">Total Students Sent</p>
          <p className="text-xs text-slate-400 mt-0.5">{periodLabel}</p>
        </div>

        {/* Total Received */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-slate-500 text-[20px]">flight_land</span>
          </div>
          {loading
            ? <Skeleton className="h-9 w-16 mb-1" />
            : <p className="font-display text-3xl font-bold text-[#0b1c30]">{totals.received}</p>
          }
          <p className="text-[#464555] text-sm mt-1">Total Students Received</p>
          <p className="text-xs text-slate-400 mt-0.5">{periodLabel}</p>
        </div>

        {/* Overall Balance */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
            totals.balance < 0 ? 'bg-red-50' : totals.balance > 0 ? 'bg-emerald-50' : 'bg-slate-100'
          }`}>
            <span className={`material-symbols-outlined text-[20px] ${
              totals.balance < 0 ? 'text-red-500' : totals.balance > 0 ? 'text-emerald-600' : 'text-slate-500'
            }`}>balance</span>
          </div>
          {loading
            ? <Skeleton className="h-9 w-16 mb-1" />
            : <p className={`font-display text-3xl font-bold ${
                totals.balance < 0 ? 'text-red-500' : totals.balance > 0 ? 'text-emerald-600' : 'text-slate-500'
              }`}>
                {totals.balance > 0 ? `+${totals.balance}` : totals.balance}
              </p>
          }
          <p className="text-[#464555] text-sm mt-1">Overall Balance</p>
          <p className={`text-xs mt-0.5 ${
            totals.balance < 0 ? 'text-red-400' : totals.balance > 0 ? 'text-emerald-500' : 'text-slate-400'
          }`}>{balanceNote}</p>
        </div>

        {/* Active Partners */}
        <div className="bg-[#4343d5] rounded-2xl p-6 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-white text-[20px]">public</span>
          </div>
          {loading
            ? <Skeleton className="h-9 w-16 mb-1 bg-white/20" />
            : <p className="font-display text-3xl font-bold text-white">{partnerCount}</p>
          }
          <p className="text-indigo-200 text-sm mt-1">Active Partners</p>
          <p className="text-xs text-indigo-300 mt-0.5">
            {loading ? '…' : `${partnerStats.length} with exchange history`}
          </p>
        </div>
      </div>

      {/* ── Section 2: Trend chart ─────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-indigo-600">show_chart</span>
            <div>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Exchange Volume Over Time</h2>
              <p className="text-xs text-slate-400 mt-0.5 capitalize">{periodLabel}</p>
            </div>
          </div>

          {/* Chart-level term selector */}
          <select
            value={termId}
            onChange={e => setTermId(e.target.value)}
            className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none cursor-pointer"
          >
            <option value="">All Terms</option>
            {semesters.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="p-6">
          {loading ? (
            <Skeleton className="h-[280px] w-full" />
          ) : chartData.length === 0 ? (
            <div className="h-[280px] flex flex-col items-center justify-center text-slate-400 gap-2">
              <span className="material-symbols-outlined text-[32px]">bar_chart</span>
              <p className="text-sm">No data available for the selected period.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} barCategoryGap="30%" barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                <Legend
                  wrapperStyle={{ paddingTop: '16px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
                  formatter={(v) => <span style={{ color: '#475569' }}>{v}</span>}
                />
                <Bar dataKey="Sent"     fill="#4343d5" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Received" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Section 3: Partner breakdown table ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Exchange History by Partner University</h2>
              <p className="text-xs text-slate-400 mt-0.5">All-time totals per institution</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]">search</span>
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search universities..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  className="pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 w-52"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); searchRef.current?.focus(); }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[16px]">close</span>
                  </button>
                )}
              </div>
              <select
                value={filter}
                onChange={e => { setFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
              >
                {['All', 'Surplus', 'Deficit', 'Balanced'].map(f => <option key={f}>{f}</option>)}
              </select>
              <select
                value={balanceSort}
                onChange={e => { setBalanceSort(e.target.value); setPage(1); }}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
              >
                <option value="">Sort by Balance</option>
                <option value="lowest">Lowest first</option>
                <option value="highest">Highest first</option>
              </select>
              {(search || filter !== 'All' || balanceSort) && (
                <button
                  onClick={() => { setSearch(''); setFilter('All'); setBalanceSort(''); setPage(1); }}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[14px]">close</span>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              {['University', 'Country', 'Type', 'Total Sent', 'Total Received', 'Balance', 'Trend', 'Actions'].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5 first:pl-6">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              // Skeleton rows while loading
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-slate-50">
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-4 py-4 first:pl-6">
                      <Skeleton className="h-4 w-full max-w-[120px]" />
                    </td>
                  ))}
                </tr>
              ))
            ) : paginatedPartners.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-slate-400">
                  {partnerStats.length === 0
                    ? 'No exchange history yet — add students with a partner university to see data here.'
                    : 'No partners match your search.'}
                </td>
              </tr>
            ) : (
              paginatedPartners.map((p, i) => {
                const trend = trendLabel(p.balance);
                return (
                  <tr
                    key={p.id ?? i}
                    onClick={() => navigate(`/analytics/partner/${p.id}`)}
                    className="border-b border-slate-50 last:border-0 hover:bg-[#F0F2FF] transition-colors cursor-pointer"
                  >
                    <td className="pl-6 pr-4 py-3.5 text-sm font-medium text-[#0b1c30]">{highlight(p.name, search)}</td>
                    <td className="px-4 py-3.5 text-sm text-[#464555]">{p.country}</td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        p.type === 'Erasmus'       ? 'bg-blue-100 text-blue-700'   :
                        p.type === 'Double Degree' ? 'bg-purple-100 text-purple-700' :
                        'bg-violet-100 text-violet-700'
                      }`}>{p.type}</span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[#464555]">{p.sent}</td>
                    <td className="px-4 py-3.5 text-sm text-[#464555]">{p.received}</td>
                    <td className={`px-4 py-3.5 text-sm ${balanceColor(p.balance)}`}>
                      {p.balance > 0 ? `+${p.balance}` : p.balance}
                    </td>
                    <td className={`px-4 py-3.5 text-sm font-medium ${trend.cls}`}>
                      {trend.icon} {trend.label}
                    </td>
                    <td className="px-4 py-3.5">
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/analytics/partner/${p.id}`); }}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 px-3 py-1.5 rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors"
                      >
                        View History
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {filteredPartners.length === 0
                ? 'No partners match your search'
                : `Showing ${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filteredPartners.length)} of ${filteredPartners.length} universities`}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[16px]">chevron_left</span>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    n === page ? 'bg-indigo-600 text-white' : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[16px]">chevron_right</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Section 4: Top senders + Rebalance ───────── */}
      <div className="grid grid-cols-2 gap-6">
        {/* Top sending partners (most incoming students to us) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-indigo-600">leaderboard</span>
            <h2 className="font-display text-base font-semibold text-[#0b1c30]">Top Sending Partners</h2>
          </div>
          <div className="p-6 space-y-3">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-6 h-6 rounded-full" />
                  <Skeleton className="flex-1 h-4" />
                  <Skeleton className="w-14 h-4" />
                </div>
              ))
            ) : topSenders.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No data yet</p>
            ) : (
              topSenders.map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0b1c30] truncate">{p.name}</p>
                  </div>
                  <span className="text-sm font-semibold text-indigo-600 flex-shrink-0">{p.sent} sent</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Partnerships to rebalance */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-red-500">warning</span>
            <h2 className="font-display text-base font-semibold text-[#0b1c30]">Partnerships to Rebalance</h2>
          </div>
          <div className="p-6 space-y-3">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-6 h-6 rounded-full" />
                  <Skeleton className="flex-1 h-4" />
                  <Skeleton className="w-10 h-6 rounded-full" />
                </div>
              ))
            ) : toRebalance.length === 0 ? (
              <div className="text-center py-4">
                <span className="material-symbols-outlined text-emerald-500 text-[28px]">check_circle</span>
                <p className="text-sm text-slate-400 mt-1">All partnerships are balanced!</p>
              </div>
            ) : (
              <>
                {toRebalance.map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-red-50 text-red-500 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0b1c30] truncate">{p.name}</p>
                    </div>
                    <span className="text-xs font-semibold bg-red-100 text-red-600 px-2.5 py-1 rounded-full flex-shrink-0">
                      {p.deficit}
                    </span>
                  </div>
                ))}
                <p className="text-xs text-slate-400 mt-4 leading-relaxed pt-2 border-t border-slate-50">
                  Consider sending more students to rebalance these partnerships
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      </>} {/* end analytics tab */}
    </Layout>
  );
}
